import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: { sitekey: string; callback: (t: string) => void; "error-callback"?: () => void; "expired-callback"?: () => void; theme?: "light" | "dark" | "auto"; size?: "normal" | "compact" | "flexible" }
      ) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
  }
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let scriptPromise: Promise<void> | null = null;
const loadScript = () => {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Turnstile script failed to load"));
    document.head.appendChild(s);
  });
  return scriptPromise;
};

let configPromise: Promise<{ siteKey: string | null; enabled: boolean }> | null = null;
const fetchConfig = () => {
  if (configPromise) return configPromise;
  configPromise = supabase.functions
    .invoke("verify-turnstile", { method: "GET" })
    .then(({ data, error }) => {
      if (error || !data) return { siteKey: null, enabled: false };
      return { siteKey: data.siteKey ?? null, enabled: !!data.enabled };
    })
    .catch(() => ({ siteKey: null, enabled: false }));
  return configPromise;
};

interface Props {
  onToken: (token: string | null) => void;
  theme?: "light" | "dark" | "auto";
}

/**
 * Renders a Cloudflare Turnstile widget if TURNSTILE_SITE_KEY is configured
 * server-side. When unconfigured, renders nothing and emits a sentinel "disabled"
 * token so forms can submit. Server-side verification stays the source of truth.
 */
export default function TurnstileWidget({ onToken, theme = "dark" }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [siteKey, setSiteKey] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchConfig().then((cfg) => {
      if (cancelled) return;
      if (!cfg.enabled || !cfg.siteKey) {
        setDisabled(true);
        // Emit a sentinel so submit handlers can proceed; server returns ok in disabled mode.
        onToken("disabled");
        return;
      }
      setSiteKey(cfg.siteKey);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!siteKey || !containerRef.current) return;
    let mounted = true;
    loadScript()
      .then(() => {
        if (!mounted || !window.turnstile || !containerRef.current) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          size: "flexible",
          callback: (t) => onToken(t),
          "expired-callback": () => onToken(null),
          "error-callback": () => onToken(null),
        });
      })
      .catch(() => onToken(null));
    return () => {
      mounted = false;
      try {
        if (widgetIdRef.current && window.turnstile?.remove) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch {
        /* noop */
      }
      widgetIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey, theme]);

  if (disabled) return null;
  return <div ref={containerRef} className="my-2 min-h-[65px]" />;
}

/** Server-side verification helper for form submit handlers. */
export async function verifyTurnstileToken(token: string | null): Promise<boolean> {
  if (!token) return false;
  if (token === "disabled") return true; // server returns ok when no secret configured
  try {
    const { data, error } = await supabase.functions.invoke("verify-turnstile", {
      body: { token },
    });
    if (error) return false;
    return !!data?.ok;
  } catch {
    return false;
  }
}
